import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useSelector } from "react-redux";
import useApi from "../../../CustomHooks/useApi";

/* ── TABLE STYLES ── */
const TH = "border border-[#94A3B8] bg-[#EFF6FF] px-3 py-2.5 text-left text-[11px] font-medium text-[#0A2A4F] uppercase tracking-wider whitespace-nowrap select-none";
const TD = "border border-[#94A3B8] px-3 py-2.5 text-sm text-gray-700 align-middle whitespace-nowrap";

/* ══════════════════════════════════════════════ */
const CdmLedger = () => {

    const username = useSelector((state) => state.auth.employeeDetails?.username);

    const { callApi: getAllCdm }           = useApi("/admin/axis/cdm/card-mapping/all", "GET");
    const { callApi: deactivateCdmApi }   = useApi("/admin/axis/cdm/card-mapping/deactivate", "POST");
    const { callApi: reactivateCdmApi }   = useApi("/admin/axis/cdm/card-mapping/reactivate", "POST");
    const { callApi: manualConfirmApi }   = useApi("/admin/axis/cdm/manual-confirmation", "POST");

    const [data, setData]                       = useState([]);
    const [loading, setLoading]                 = useState(false);
    const [excelLoading, setExcelLoading]       = useState(false);
    const [actionLoadingId, setActionLoadingId] = useState(null);
    const [toast, setToast]                     = useState(null);
    const [searchMobile, setSearchMobile]       = useState("");

    /* ── manual confirmation modal ── */
    const [showManualModal, setShowManualModal] = useState(false);
    const [manualUtr, setManualUtr]             = useState("");
    const [manualCardNo, setManualCardNo]       = useState("");
    const [manualTxnAmt, setManualTxnAmt]       = useState("");
    const [manualPin, setManualPin]             = useState("");
    const [manualLoading, setManualLoading]     = useState(false);
    const [manualError, setManualError]         = useState("");

    /* ── toast ── */
    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3500);
    };

    /* ── fetch ── */
    const fetchAll = async () => {
        setLoading(true);
        try {
            const res = await getAllCdm(null, null);
            const rows = res?.content ?? (Array.isArray(res) ? res : []);
            setData(rows);
        } catch (err) {
            setData([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    /* ── filtered rows ── */
    const filteredData = searchMobile.trim()
        ? data.filter((row) =>
            String(row.mobileNumber ?? "").includes(searchMobile.trim())
        )
        : data;

    /* ── deactivate / reactivate ── */
    const handleDeactivate = async (cdmCardNo) => {
        setActionLoadingId(cdmCardNo);
        try {
            const res = await deactivateCdmApi(null, `?cdmCardNo=${cdmCardNo}`);
            showToast(res?.success !== false ? "success" : "error", res?.message || "Card deactivated successfully");
            fetchAll();
        } catch (err) {
            showToast("error", err?.message || "Deactivation failed");
        } finally {
            setActionLoadingId(null);
        }
    };

    const handleReactivate = async (cdmCardNo) => {
        setActionLoadingId(cdmCardNo);
        try {
            const res = await reactivateCdmApi(null, `?cdmCardNo=${cdmCardNo}`);
            showToast(res?.success !== false ? "success" : "error", res?.message || "Card reactivated successfully");
            fetchAll();
        } catch (err) {
            showToast("error", err?.message || "Reactivation failed");
        } finally {
            setActionLoadingId(null);
        }
    };

    /* ── manual confirmation ── */
    const closeManualModal = () => {
        setShowManualModal(false);
        setManualUtr(""); setManualCardNo(""); setManualTxnAmt("");
        setManualPin(""); setManualError("");
    };

    const isManualValid =
        manualUtr.trim().length > 0 &&
        manualCardNo.trim().length > 0 &&
        manualTxnAmt.trim().length > 0 &&
        !isNaN(Number(manualTxnAmt)) &&
        Number(manualTxnAmt) > 0 &&
        manualPin === "971888";

    const handleManualConfirm = async () => {
        if (!isManualValid) {
            if (!manualUtr.trim())                      { setManualError("UTR Number is required."); return; }
            if (!manualCardNo.trim())                   { setManualError("CDM Card Number is required."); return; }
            if (!manualTxnAmt.trim() || isNaN(Number(manualTxnAmt)) || Number(manualTxnAmt) <= 0)
                                                        { setManualError("Enter a valid transaction amount."); return; }
            if (manualPin !== "971888")                 { setManualError("Incorrect security PIN."); return; }
            return;
        }
        setManualError("");
        setManualLoading(true);
        try {
            const res = await manualConfirmApi({
                utr:       manualUtr,
                cdmCardNo: manualCardNo.trim(),
                txnAmnt:   manualTxnAmt.trim(),
            });
            showToast(res?.success !== false ? "success" : "error", res?.message || "Manual confirmation successful");
            if (res?.success !== false) closeManualModal();
        } catch (err) {
            setManualError(err?.response?.data?.message || err?.message || "Manual confirmation failed");
        } finally {
            setManualLoading(false);
        }
    };

    /* ── excel download ── */
    const downloadExcel = () => {
        const rows = filteredData.map((row, idx) => ({
            "#": idx + 1,
            "ID": row.id ?? "-",
            "CDM Card No": row.cdmCardNo ?? "-",
            "Agent Emboss Code": row.agentEmbossCode ?? "-",
            "Shop Name": row.shopName ?? "-",
            "Mobile Number": row.mobileNumber ?? "-",
            "BP User ID": row.bpUserId ?? "-",
            "Status": row.active ? "Active" : "Inactive",
            "Created At": row.createdAt ?? "-",
        }));
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "CDM");
        const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        saveAs(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "cdm_card_mapping.xlsx");
    };

    /* ══════════════ RENDER ══════════════ */
    return (
        <div className="w-full mx-auto pb-24">

            {/* TOAST */}
            {toast && (
                <div className={`fixed top-5 right-5 z-[60] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg border text-sm font-medium transition-all
                    ${toast.type === "success" ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                    <span>{toast.type === "success" ? "✓" : "✕"}</span>
                    <span>{toast.message}</span>
                    <button onClick={() => setToast(null)} className="ml-2 opacity-60 hover:opacity-100 text-lg leading-none">✕</button>
                </div>
            )}

            {/* HEADER */}
            <div className="mb-6 bg-gradient-to-r from-[#0A2A4F] to-[#1a4a8f] rounded-2xl p-6 text-white shadow-lg">
                <h2 className="text-2xl font-bold">CDM Card Mapping</h2>
                <p className="text-blue-200 text-sm mt-1">View and manage all CDM card mapping records</p>
            </div>

            {/* TABLE CARD */}
            <div className="bg-white rounded-2xl shadow border overflow-hidden">

                {/* Card header */}
                <div className="px-5 py-4 border-b flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h3 className="text-base font-bold text-gray-800">CDM Card Mapping</h3>
                        <p className="text-xs text-gray-400 mt-0.5">
                            {filteredData.length.toLocaleString("en-IN")} record{filteredData.length !== 1 ? "s" : ""}
                            {searchMobile.trim() && ` (filtered from ${data.length})`}
                        </p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                        {/* Search by mobile */}
                        <div className="flex items-center gap-1.5 border border-[#E5E7EB] rounded-xl overflow-hidden bg-white">
                            <input
                                type="text"
                                value={searchMobile}
                                onChange={(e) => setSearchMobile(e.target.value)}
                                placeholder="Search by mobile..."
                                className="h-[38px] px-3 text-sm text-gray-700 outline-none w-48"
                            />
                            {searchMobile && (
                                <button
                                    onClick={() => setSearchMobile("")}
                                    className="pr-3 text-gray-400 hover:text-gray-600 text-lg leading-none"
                                >
                                    ✕
                                </button>
                            )}
                        </div>

                        <button
                            onClick={fetchAll}
                            disabled={loading}
                            className="flex items-center gap-2 bg-[#0A2A4F] hover:bg-[#0d3566] disabled:opacity-50 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition-colors"
                        >
                            {loading ? "Loading..." : "↻ Refresh"}
                        </button>
                        <button
                            onClick={downloadExcel}
                            disabled={filteredData.length === 0}
                            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
                        >
                            {`↓ Download Excel (${filteredData.length})`}
                        </button>
                        {username === "Naveen1908" && (
                            <button
                                onClick={() => setShowManualModal(true)}
                                className="flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-5 py-2.5 rounded-xl transition-colors"
                            >
                                ⚡ Card-Manual-Confirmation
                            </button>
                        )}
                    </div>
                </div>

                {/* TABLE */}
                <div className="overflow-x-auto">
                    {loading ? (
                        <div className="flex flex-col items-center gap-3 py-20">
                            <div className="w-10 h-10 border-4 border-[#0A2A4F] border-t-transparent rounded-full animate-spin"></div>
                            <p className="text-gray-400 text-sm">Fetching CDM records...</p>
                        </div>
                    ) : (
                        <table className="border-collapse text-sm" style={{ minWidth: 900, width: "100%" }}>
                            <thead>
                                <tr>
                                    <th className={TH}>#</th>
                                    <th className={TH}>CDM Card No</th>
                                    <th className={TH}>Agent Emboss Code</th>
                                    <th className={TH}>Shop Name</th>
                                    <th className={TH}>Mobile Number</th>
                                    <th className={TH}>BP User ID</th>
                                    <th className={TH}>Status</th>
                                    <th className={TH}>Created At</th>
                                    <th className={TH}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredData.length === 0 ? (
                                    <tr>
                                        <td colSpan={9} className="border border-[#94A3B8] py-20 text-center">
                                            <div className="flex flex-col items-center gap-3">
                                                <div className="w-14 h-14 rounded-2xl bg-gray-100 flex items-center justify-center text-3xl shadow-inner">📭</div>
                                                <p className="text-gray-500 text-sm font-medium">
                                                    {searchMobile.trim() ? "No records match this mobile number" : "No CDM card mapping records found"}
                                                </p>
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    filteredData.map((row, idx) => {
                                        const isActive = row.active === true;
                                        const cardNo = row.cdmCardNo ?? "";
                                        const isActioning = actionLoadingId === cardNo;
                                        return (
                                            <tr key={row.id ?? idx} className={idx % 2 === 0 ? "bg-white" : "bg-[#F8FAFC]"}>
                                                <td className={TD}>{idx + 1}</td>
                                                <td className={`${TD} font-mono font-semibold text-gray-900`}>{cardNo || "-"}</td>
                                                <td className={TD}>{row.agentEmbossCode ?? "-"}</td>
                                                <td className={TD}>{row.shopName ?? "-"}</td>
                                                <td className={TD}>{row.mobileNumber ?? "-"}</td>
                                                <td className={TD}>{row.bpUserId ?? "-"}</td>
                                                <td className={TD}>
                                                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-lg text-xs font-semibold border
                                                        ${isActive ? "bg-green-100 text-green-700 border-green-200" : "bg-red-100 text-red-700 border-red-200"}`}>
                                                        {isActive ? "Active" : "Inactive"}
                                                    </span>
                                                </td>
                                                <td className={TD}>
                                                    {row.createdAt
                                                        ? new Date(row.createdAt).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                                                        : "-"}
                                                </td>
                                                <td className={TD}>
                                                    {isActive ? (
                                                        <button
                                                            onClick={() => cardNo && handleDeactivate(cardNo)}
                                                            disabled={isActioning || !cardNo}
                                                            className="inline-flex items-center gap-1.5 bg-red-600 hover:bg-red-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                                                        >
                                                            {isActioning ? (
                                                                <>
                                                                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                                                                    Deactivating...
                                                                </>
                                                            ) : "Deactivate"}
                                                        </button>
                                                    ) : (
                                                        <button
                                                            onClick={() => cardNo && handleReactivate(cardNo)}
                                                            disabled={isActioning || !cardNo}
                                                            className="inline-flex items-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed text-white text-xs font-semibold px-3 py-1.5 rounded-lg transition-colors whitespace-nowrap"
                                                        >
                                                            {isActioning ? (
                                                                <>
                                                                    <span className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin inline-block" />
                                                                    Reactivating...
                                                                </>
                                                            ) : "Reactivate"}
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })
                                )}
                            </tbody>
                        </table>
                    )}
                </div>

            </div>

        {/* ══ MANUAL CONFIRMATION MODAL ══ */}
        {showManualModal && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm" onClick={closeManualModal}>
                <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>

                    {/* Modal header */}
                    <div className="bg-gradient-to-r from-indigo-600 to-indigo-800 px-6 py-5">
                        <h2 className="text-lg font-bold text-white">Card Manual Confirmation</h2>
                        <p className="text-indigo-200 text-xs mt-0.5">Submit a manual CDM transaction confirmation</p>
                    </div>

                    {/* Modal body */}
                    <div className="px-6 py-6 space-y-5">

                        {/* UTR Number */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                                UTR Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={manualUtr}
                                onChange={(e) => {
                                    setManualUtr(e.target.value);
                                    setManualError("");
                                }}
                                placeholder="e.g. TXN2408260001AB"
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono outline-none transition focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400"
                            />
                        </div>

                        {/* CDM Card Number */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                                CDM Card Number <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={manualCardNo}
                                onChange={(e) => { setManualCardNo(e.target.value); setManualError(""); }}
                                placeholder="e.g. 5046430003843855"
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm font-mono outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition"
                            />
                        </div>

                        {/* Transaction Amount */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                                Transaction Amount (₹) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                inputMode="decimal"
                                value={manualTxnAmt}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/[^0-9.]/g, "");
                                    setManualTxnAmt(val);
                                    setManualError("");
                                }}
                                placeholder="e.g. 100000"
                                className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-indigo-200 focus:border-indigo-400 transition"
                            />
                        </div>

                        {/* Security PIN */}
                        <div>
                            <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide mb-1.5">
                                Security PIN <span className="text-red-500">*</span>
                                <span className="ml-2 font-normal text-gray-400 normal-case">6-digit authorization PIN</span>
                            </label>
                            <input
                                type="password"
                                inputMode="numeric"
                                maxLength={6}
                                value={manualPin}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
                                    setManualPin(val);
                                    setManualError("");
                                }}
                                placeholder="••••••"
                                className={`w-full border rounded-xl px-4 py-3 text-sm tracking-[0.4em] outline-none transition focus:ring-2
                                    ${manualPin.length === 6 && manualPin !== "971888"
                                        ? "border-red-300 focus:ring-red-200"
                                        : manualPin.length === 6 && manualPin === "971888"
                                            ? "border-green-300 focus:ring-green-200"
                                            : "border-gray-200 focus:ring-indigo-200 focus:border-indigo-400"}`}
                            />
                            {manualPin.length === 6 && manualPin !== "971888" && (
                                <p className="text-xs text-red-500 mt-1">Incorrect PIN. Please try again.</p>
                            )}
                            {manualPin.length === 6 && manualPin === "971888" && (
                                <p className="text-xs text-green-600 mt-1">✓ PIN verified</p>
                            )}
                        </div>

                        {/* API error */}
                        {manualError && (
                            <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-600 font-medium">
                                ⚠ {manualError}
                            </div>
                        )}
                    </div>

                    {/* Modal footer */}
                    <div className="px-6 pb-6 flex gap-3">
                        <button
                            onClick={closeManualModal}
                            disabled={manualLoading}
                            className="flex-1 py-3 rounded-xl bg-gray-100 hover:bg-gray-200 disabled:opacity-50 text-gray-700 font-semibold text-sm transition"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={handleManualConfirm}
                            disabled={!isManualValid || manualLoading}
                            className="flex-1 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold text-sm transition flex items-center justify-center gap-2"
                        >
                            {manualLoading ? (
                                <>
                                    <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    Confirming...
                                </>
                            ) : "Confirm"}
                        </button>
                    </div>

                </div>
            </div>
        )}

    </div>
    );
};

export default CdmLedger;
